import { useState } from 'react';
import {
  BUNDLE_VERSION,
  applyNow,
  checkNow,
  liveUpdateEnabled,
  useUpdateStatus,
  type CheckResult,
} from '../lib/liveUpdate';

type State = 'idle' | 'checking' | 'applying' | 'failed' | CheckResult;

/**
 * 版本号 + 「检查更新」,放在侧栏底部。
 *
 * 平时不用点 —— 每次回到前台都会自己查、后台下载、切后台时换上。
 * 按钮是给「刚发了新版、我现在就要」的时候用的。
 *
 * ★ 网页版只显示版本号:浏览器里刷新就是最新,没有「装在本地的旧包」。
 */
export function UpdateCheck() {
  const [checked, setState] = useState<State>('idle');
  /*
   * ★ 后台自己查到、下好的新版本,这里也要认 —— 不然提示条说「新版本已就绪」,
   *   点进来这里还是一个「检查更新」,让人以为是两回事。
   */
  const status = useUpdateStatus();
  const state: State =
    checked === 'checking' || checked === 'applying'
      ? checked
      : status.kind === 'ready'
        ? 'staged'
        : status.kind === 'reinstall'
          ? 'native-too-old'
          : checked;

  const check = async () => {
    setState('checking');
    try {
      setState(await checkNow());
    } catch {
      setState('failed');
    }
  };

  const apply = async () => {
    setState('applying');
    try {
      await applyNow(); // 成功的话页面已经重载,走不到下面
    } catch {
      setState('failed');
    }
  };

  const msg: Partial<Record<State, string>> = {
    'up-to-date': '已是最新版本',
    'known-bad': '已是最新版本',
    'native-too-old': '新版本需要重新安装 App',
    'bad-manifest': '检查失败,请稍后再试',
    failed: '检查失败,请检查网络',
  };

  return (
    <div className="px-6 pb-3 text-xs text-slate-400 space-y-1">
      <div>版本 {BUNDLE_VERSION}</div>
      {liveUpdateEnabled &&
        (state === 'staged' || state === 'applying' ? (
          <div className="space-y-1">
            <div className="text-slate-600 dark:text-slate-300">新版本已下载</div>
            <button
              onClick={apply}
              disabled={state === 'applying'}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-white disabled:opacity-60"
            >
              {state === 'applying' ? '正在更新…' : '立即更新'}
            </button>
          </div>
        ) : (
          <div>
            <button
              onClick={check}
              disabled={state === 'checking'}
              className="text-brand-600 dark:text-brand-400 disabled:opacity-60"
            >
              {state === 'checking' ? '正在检查…' : '检查更新'}
            </button>
            {msg[state] && <div>{msg[state]}</div>}
          </div>
        ))}
    </div>
  );
}
